import { Component, Input, Output, EventEmitter } from "@angular/core";

// AR-32: extracted from questionnaire.component so the exact same per-type
// render chain (text/number/date/boolean/radio/select/checkbox/signature) can
// be reused both for a top-level question AND for each row of a repeatable
// group's sub-questions, instead of duplicating ~80 lines of markup per use.
// `key` is the caller-supplied answers/otherText map key - the parent decides
// whether that's `String(q.AutoIndex)` (top level) or a per-row composite key
// (inside a group) - this component never derives a key from `q` itself,
// which is what let AR-24's original single-answer-per-question assumption
// (answers keyed bare by AutoIndex) break cleanly instead of silently
// colliding across repeated rows.
@Component({
  selector: "app-question-field",
  templateUrl: "./question-field.component.html",
  styleUrls: ["./question-field.component.scss"],
  standalone: false,
})
export class QuestionFieldComponent {
  @Input() q: any;
  @Input() key = "";
  @Input() answers: { [key: string]: any } = {};
  @Input() otherText: { [key: string]: string } = {};
  @Input() lang: "he" | "en" = "he";
  @Input() dirValue = "rtl";
  @Input() invalid = false;
  // AR-32: each question now renders inside its own <app-question-field> host
  // element, so CSS ":first-child" on the inner .q-question div no longer
  // identifies the first VISIBLE question on the page (it's always true,
  // since the div is the sole child of its own host) - the parent computes
  // this explicitly instead and passes it down.
  @Input() first = false;

  // AR-32: fired on a *committed* change (native `change`, not every
  // keystroke) so the page can re-derive auto-mode group row counts from a
  // "how many deliveries" answer without reacting to "1" while the patient is
  // still typing "12" (which would destroy 11 rows mid-keystroke).
  @Output() committed = new EventEmitter<void>();

  qText(): string {
    return this.lang === "en" && this.q.questionTextEng ? this.q.questionTextEng : this.q.questionText;
  }
  oText(o: any): string {
    return this.lang === "en" && o.optionTextEng ? o.optionTextEng : o.optionText;
  }

  dateBound(which: "dateMin" | "dateMax"): string {
    const bound = this.q[which];
    if (!bound) return "";
    if (bound === "today") {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    }
    return bound;
  }

  // '' | 'text' | 'number' — falls back to the legacy allowFreeText boolean
  // for options saved before freeTextType existed (treated as 'text').
  extraFieldType(o: any): string {
    return (o && (o.freeTextType || (o.allowFreeText ? "text" : ""))) || "";
  }
  optionHasExtraField(o: any): boolean {
    return !!this.extraFieldType(o);
  }
  selectedOptionExtraFieldType(): string {
    const selected = this.answers[this.key];
    if (!selected) return "";
    const opt = (this.q.options || []).find((o: any) => o.optionText === selected);
    return opt ? this.extraFieldType(opt) : "";
  }
  otherTextValue(subKey: string): string {
    return this.otherText[subKey] || "";
  }
  setOtherText(subKey: string, value: string): void {
    this.otherText[subKey] = value;
  }
  checkboxOtherKey(optionText: string): string {
    return `${this.key}:${optionText}`;
  }

  isChecked(val: string): boolean {
    const arr = this.answers[this.key] || [];
    return arr.indexOf(val) >= 0;
  }
  toggleCheckbox(val: string, checked: boolean): void {
    const arr = this.answers[this.key] || [];
    if (checked) {
      if (arr.indexOf(val) < 0) arr.push(val);
    } else {
      const i = arr.indexOf(val);
      if (i >= 0) arr.splice(i, 1);
    }
    this.answers[this.key] = arr;
  }
  setSignature(dataUrl: string): void {
    this.answers[this.key] = dataUrl;
  }
}
